// =============================================================================
// FORM TEMPLATES - Real-world form examples
// =============================================================================

import type { Form } from './types';

export interface FormTemplate {
  id: string;
  name: string;
  description: string;
  category: 'contact' | 'hr' | 'survey' | 'event' | 'business' | 'healthcare';
  icon: string;
  form: Form;
}

const generateId = () => crypto.randomUUID();

// =============================================================================
// 1. FORMULAIRE DE CONTACT
// =============================================================================
const contactForm: Form = {
  id: generateId(),
  name: 'Formulaire de contact',
  description: 'Formulaire de contact professionnel pour votre site web',
  sections: [
    {
      id: generateId(),
      title: 'Contactez-nous',
      description: 'Remplissez ce formulaire et nous vous répondrons dans les plus brefs délais.',
      fields: [
        {
          id: generateId(),
          type: 'text',
          name: 'nom',
          label: 'Nom complet',
          placeholder: 'Jean Dupont',
          validation: [{ type: 'required', message: 'Le nom est obligatoire' }],
          width: 'half',
        },
        {
          id: generateId(),
          type: 'email',
          name: 'email',
          label: 'Adresse email',
          placeholder: 'jean.dupont@email.com',
          validation: [
            { type: 'required', message: "L'email est obligatoire" },
            { type: 'email', message: 'Veuillez entrer un email valide' },
          ],
          width: 'half',
        },
        {
          id: generateId(),
          type: 'phone',
          name: 'telephone',
          label: 'Téléphone',
          placeholder: '+33 6 12 34 56 78',
          validation: [],
          width: 'half',
        },
        {
          id: generateId(),
          type: 'select',
          name: 'sujet',
          label: 'Sujet',
          placeholder: 'Sélectionnez un sujet',
          options: [
            { label: 'Demande d\'information', value: 'info' },
            { label: 'Support technique', value: 'support' },
            { label: 'Partenariat', value: 'partenariat' },
            { label: 'Réclamation', value: 'reclamation' },
            { label: 'Autre', value: 'autre' },
          ],
          validation: [{ type: 'required', message: 'Veuillez sélectionner un sujet' }],
          width: 'half',
        },
        {
          id: generateId(),
          type: 'textarea',
          name: 'message',
          label: 'Votre message',
          placeholder: 'Décrivez votre demande en détail...',
          rows: 5,
          validation: [
            { type: 'required', message: 'Le message est obligatoire' },
            { type: 'minLength', value: 20, message: 'Le message doit contenir au moins 20 caractères' },
          ],
          width: 'full',
        },
        {
          id: generateId(),
          type: 'checkbox',
          name: 'newsletter',
          label: 'Newsletter',
          description: 'Je souhaite recevoir les actualités et offres par email',
          validation: [],
          width: 'full',
        },
        {
          id: generateId(),
          type: 'checkbox',
          name: 'rgpd',
          label: 'Consentement RGPD',
          description: 'J\'accepte que mes données soient traitées conformément à la politique de confidentialité',
          validation: [{ type: 'required', message: 'Vous devez accepter les conditions' }],
          width: 'full',
        },
      ],
    },
  ],
  settings: {
    submitLabel: 'Envoyer le message',
    resetLabel: 'Effacer',
    successMessage: 'Merci pour votre message ! Nous vous répondrons dans les 24 heures.',
    allowMultipleSubmissions: true,
    showProgressBar: false,
    showPageNumbers: false,
    showRequiredAsterisk: true,
    layout: 'card',
    labelPosition: 'top',
  },
  theme: {
    primaryColor: '#3B82F6',
    backgroundColor: '#F9FAFB',
    textColor: '#1F2937',
    borderRadius: 'lg',
    fontSize: 'md',
    spacing: 'normal',
  },
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

// =============================================================================
// 2. CANDIDATURE EMPLOI
// =============================================================================
const jobApplicationForm: Form = {
  id: generateId(),
  name: 'Candidature emploi',
  description: 'Formulaire de candidature pour un poste',
  sections: [
    {
      id: generateId(),
      title: 'Informations personnelles',
      description: 'Vos coordonnées et informations de base',
      fields: [
        {
          id: generateId(),
          type: 'text',
          name: 'prenom',
          label: 'Prénom',
          placeholder: 'Jean',
          validation: [{ type: 'required', message: 'Le prénom est obligatoire' }],
          width: 'half',
        },
        {
          id: generateId(),
          type: 'text',
          name: 'nom',
          label: 'Nom',
          placeholder: 'Dupont',
          validation: [{ type: 'required', message: 'Le nom est obligatoire' }],
          width: 'half',
        },
        {
          id: generateId(),
          type: 'email',
          name: 'email',
          label: 'Email',
          placeholder: 'jean.dupont@email.com',
          validation: [
            { type: 'required', message: "L'email est obligatoire" },
            { type: 'email', message: 'Email invalide' },
          ],
          width: 'half',
        },
        {
          id: generateId(),
          type: 'phone',
          name: 'telephone',
          label: 'Téléphone',
          placeholder: '+33 6 12 34 56 78',
          validation: [{ type: 'required', message: 'Le téléphone est obligatoire' }],
          width: 'half',
        },
        {
          id: generateId(),
          type: 'text',
          name: 'adresse',
          label: 'Adresse',
          placeholder: '123 Rue de la Paix',
          validation: [],
          width: 'full',
        },
        {
          id: generateId(),
          type: 'text',
          name: 'ville',
          label: 'Ville',
          placeholder: 'Paris',
          validation: [{ type: 'required', message: 'La ville est obligatoire' }],
          width: 'third',
        },
        {
          id: generateId(),
          type: 'text',
          name: 'code_postal',
          label: 'Code postal',
          placeholder: '75001',
          validation: [{ type: 'required', message: 'Le code postal est obligatoire' }],
          width: 'third',
        },
        {
          id: generateId(),
          type: 'select',
          name: 'pays',
          label: 'Pays',
          placeholder: 'Sélectionnez',
          options: [
            { label: 'France', value: 'FR' },
            { label: 'Belgique', value: 'BE' },
            { label: 'Suisse', value: 'CH' },
            { label: 'Canada', value: 'CA' },
            { label: 'Autre', value: 'OTHER' },
          ],
          validation: [{ type: 'required', message: 'Le pays est obligatoire' }],
          width: 'third',
        },
      ],
    },
    {
      id: generateId(),
      title: 'Poste souhaité',
      description: 'Informations sur le poste pour lequel vous postulez',
      fields: [
        {
          id: generateId(),
          type: 'select',
          name: 'poste',
          label: 'Poste visé',
          placeholder: 'Sélectionnez un poste',
          options: [
            { label: 'Développeur Full Stack', value: 'dev_fullstack' },
            { label: 'Développeur Frontend', value: 'dev_frontend' },
            { label: 'Développeur Backend', value: 'dev_backend' },
            { label: 'Designer UX/UI', value: 'designer' },
            { label: 'Chef de projet', value: 'pm' },
            { label: 'Data Analyst', value: 'data' },
          ],
          validation: [{ type: 'required', message: 'Veuillez sélectionner un poste' }],
          width: 'half',
        },
        {
          id: generateId(),
          type: 'select',
          name: 'contrat',
          label: 'Type de contrat',
          placeholder: 'Sélectionnez',
          options: [
            { label: 'CDI', value: 'cdi' },
            { label: 'CDD', value: 'cdd' },
            { label: 'Stage', value: 'stage' },
            { label: 'Alternance', value: 'alternance' },
            { label: 'Freelance', value: 'freelance' },
          ],
          validation: [{ type: 'required', message: 'Veuillez sélectionner un type de contrat' }],
          width: 'half',
        },
        {
          id: generateId(),
          type: 'date',
          name: 'disponibilite',
          label: 'Date de disponibilité',
          validation: [{ type: 'required', message: 'Veuillez indiquer votre disponibilité' }],
          width: 'half',
        },
        {
          id: generateId(),
          type: 'number',
          name: 'salaire',
          label: 'Prétentions salariales (€/an)',
          placeholder: '45000',
          validation: [],
          width: 'half',
        },
      ],
    },
    {
      id: generateId(),
      title: 'Expérience et compétences',
      fields: [
        {
          id: generateId(),
          type: 'slider',
          name: 'experience',
          label: 'Années d\'expérience',
          minValue: 0,
          maxValue: 20,
          step: 1,
          showValue: true,
          validation: [],
          width: 'full',
        },
        {
          id: generateId(),
          type: 'checkboxgroup',
          name: 'competences',
          label: 'Compétences techniques',
          options: [
            { label: 'JavaScript / TypeScript', value: 'js' },
            { label: 'React / Vue / Angular', value: 'frontend' },
            { label: 'Node.js / Python / Java', value: 'backend' },
            { label: 'SQL / NoSQL', value: 'database' },
            { label: 'Cloud (AWS / GCP / Azure)', value: 'cloud' },
            { label: 'DevOps / CI/CD', value: 'devops' },
          ],
          validation: [],
          width: 'full',
        },
        {
          id: generateId(),
          type: 'textarea',
          name: 'motivation',
          label: 'Lettre de motivation',
          placeholder: 'Expliquez pourquoi vous souhaitez rejoindre notre équipe...',
          rows: 6,
          validation: [
            { type: 'required', message: 'La lettre de motivation est obligatoire' },
            { type: 'minLength', value: 100, message: 'Minimum 100 caractères' },
          ],
          width: 'full',
        },
        {
          id: generateId(),
          type: 'file',
          name: 'cv',
          label: 'CV (PDF)',
          maxFiles: 1,
          maxFileSize: 5,
          acceptedFileTypes: ['application/pdf'],
          validation: [{ type: 'required', message: 'Le CV est obligatoire' }],
          width: 'full',
        },
      ],
    },
  ],
  settings: {
    submitLabel: 'Envoyer ma candidature',
    successMessage: 'Merci pour votre candidature ! Notre équipe RH vous contactera sous 5 jours ouvrés.',
    allowMultipleSubmissions: false,
    showProgressBar: true,
    showPageNumbers: true,
    showRequiredAsterisk: true,
    layout: 'card',
    labelPosition: 'top',
  },
  theme: {
    primaryColor: '#8B5CF6',
    backgroundColor: '#FFFFFF',
    textColor: '#1F2937',
    borderRadius: 'lg',
    fontSize: 'md',
    spacing: 'relaxed',
  },
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

// =============================================================================
// 3. ENQUÊTE DE SATISFACTION
// =============================================================================
const satisfactionSurvey: Form = {
  id: generateId(),
  name: 'Enquête de satisfaction',
  description: 'Questionnaire de satisfaction client',
  sections: [
    {
      id: generateId(),
      title: 'Évaluation de nos services',
      description: 'Aidez-nous à améliorer nos services en partageant votre expérience.',
      fields: [
        {
          id: generateId(),
          type: 'heading',
          name: 'heading_general',
          label: 'Satisfaction générale',
          headingLevel: 'h3',
          validation: [],
          width: 'full',
        },
        {
          id: generateId(),
          type: 'nps',
          name: 'nps_score',
          label: 'Recommanderiez-vous nos services à un ami ou collègue ?',
          helpText: '0 = Pas du tout probable, 10 = Très probable',
          validation: [{ type: 'required', message: 'Cette question est obligatoire' }],
          width: 'full',
        },
        {
          id: generateId(),
          type: 'rating',
          name: 'satisfaction_globale',
          label: 'Note globale de satisfaction',
          ratingCount: 5,
          validation: [{ type: 'required', message: 'Cette question est obligatoire' }],
          width: 'full',
        },
        {
          id: generateId(),
          type: 'divider',
          name: 'divider1',
          label: '',
          validation: [],
          width: 'full',
        },
        {
          id: generateId(),
          type: 'heading',
          name: 'heading_details',
          label: 'Détails par catégorie',
          headingLevel: 'h3',
          validation: [],
          width: 'full',
        },
        {
          id: generateId(),
          type: 'matrix',
          name: 'evaluation_details',
          label: 'Évaluez chaque aspect de notre service',
          matrixRows: [
            { value: 'qualite', label: 'Qualité du produit/service' },
            { value: 'rapidite', label: 'Rapidité de livraison' },
            { value: 'support', label: 'Support client' },
            { value: 'prix', label: 'Rapport qualité/prix' },
            { value: 'site', label: 'Expérience sur le site web' },
          ],
          matrixColumns: [
            { value: '1', label: 'Très insatisfait' },
            { value: '2', label: 'Insatisfait' },
            { value: '3', label: 'Neutre' },
            { value: '4', label: 'Satisfait' },
            { value: '5', label: 'Très satisfait' },
          ],
          validation: [],
          width: 'full',
        },
      ],
    },
    {
      id: generateId(),
      title: 'Vos commentaires',
      fields: [
        {
          id: generateId(),
          type: 'radio',
          name: 'premier_achat',
          label: 'Est-ce votre premier achat chez nous ?',
          options: [
            { label: 'Oui, c\'est ma première commande', value: 'oui' },
            { label: 'Non, je suis client régulier', value: 'non' },
          ],
          validation: [{ type: 'required', message: 'Veuillez répondre à cette question' }],
          width: 'full',
        },
        {
          id: generateId(),
          type: 'checkboxgroup',
          name: 'points_positifs',
          label: 'Qu\'avez-vous le plus apprécié ?',
          options: [
            { label: 'La qualité des produits', value: 'qualite' },
            { label: 'Les prix compétitifs', value: 'prix' },
            { label: 'La livraison rapide', value: 'livraison' },
            { label: 'Le service client', value: 'service' },
            { label: 'La facilité de commande', value: 'facilite' },
          ],
          validation: [],
          width: 'full',
        },
        {
          id: generateId(),
          type: 'textarea',
          name: 'ameliorations',
          label: 'Que pourrions-nous améliorer ?',
          placeholder: 'Partagez vos suggestions...',
          rows: 4,
          validation: [],
          width: 'full',
        },
        {
          id: generateId(),
          type: 'boolean',
          name: 'contact_retour',
          label: 'Souhaitez-vous être recontacté ?',
          description: 'Un membre de notre équipe vous appellera pour discuter de votre expérience',
          validation: [],
          width: 'full',
        },
        {
          id: generateId(),
          type: 'email',
          name: 'email',
          label: 'Votre email (optionnel)',
          placeholder: 'pour recevoir une réponse',
          validation: [],
          width: 'full',
        },
      ],
    },
  ],
  settings: {
    submitLabel: 'Envoyer mes réponses',
    successMessage: 'Merci pour votre participation ! Votre avis est précieux pour nous.',
    allowMultipleSubmissions: false,
    showProgressBar: true,
    showPageNumbers: true,
    showRequiredAsterisk: true,
    layout: 'default',
    labelPosition: 'top',
  },
  theme: {
    primaryColor: '#10B981',
    backgroundColor: '#F0FDF4',
    textColor: '#1F2937',
    borderRadius: 'lg',
    fontSize: 'md',
    spacing: 'relaxed',
  },
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

// =============================================================================
// 4. INSCRIPTION ÉVÉNEMENT
// =============================================================================
const eventRegistrationForm: Form = {
  id: generateId(),
  name: 'Inscription événement',
  description: 'Formulaire d\'inscription à un événement ou conférence',
  sections: [
    {
      id: generateId(),
      title: 'Inscription à la conférence Tech 2025',
      description: '15-16 Mars 2025 - Paris, La Défense',
      fields: [
        {
          id: generateId(),
          type: 'paragraph',
          name: 'intro',
          label: 'Introduction',
          description: 'Rejoignez-nous pour deux jours de conférences, ateliers et networking avec les experts de l\'industrie tech.',
          validation: [],
          width: 'full',
        },
        {
          id: generateId(),
          type: 'text',
          name: 'prenom',
          label: 'Prénom',
          placeholder: 'Jean',
          validation: [{ type: 'required', message: 'Le prénom est obligatoire' }],
          width: 'half',
        },
        {
          id: generateId(),
          type: 'text',
          name: 'nom',
          label: 'Nom',
          placeholder: 'Dupont',
          validation: [{ type: 'required', message: 'Le nom est obligatoire' }],
          width: 'half',
        },
        {
          id: generateId(),
          type: 'email',
          name: 'email',
          label: 'Email professionnel',
          placeholder: 'jean.dupont@entreprise.com',
          validation: [
            { type: 'required', message: "L'email est obligatoire" },
            { type: 'email', message: 'Email invalide' },
          ],
          width: 'half',
        },
        {
          id: generateId(),
          type: 'phone',
          name: 'telephone',
          label: 'Téléphone',
          placeholder: '+33 6 12 34 56 78',
          validation: [],
          width: 'half',
        },
        {
          id: generateId(),
          type: 'text',
          name: 'entreprise',
          label: 'Entreprise',
          placeholder: 'Nom de votre entreprise',
          validation: [{ type: 'required', message: "L'entreprise est obligatoire" }],
          width: 'half',
        },
        {
          id: generateId(),
          type: 'text',
          name: 'fonction',
          label: 'Fonction',
          placeholder: 'Votre poste',
          validation: [{ type: 'required', message: 'La fonction est obligatoire' }],
          width: 'half',
        },
      ],
    },
    {
      id: generateId(),
      title: 'Choix des sessions',
      fields: [
        {
          id: generateId(),
          type: 'radio',
          name: 'pass_type',
          label: 'Type de pass',
          options: [
            { label: 'Pass 1 jour (15 mars) - 150€', value: 'jour1' },
            { label: 'Pass 1 jour (16 mars) - 150€', value: 'jour2' },
            { label: 'Pass 2 jours - 250€', value: 'full' },
            { label: 'Pass VIP (accès exclusif + dîner) - 500€', value: 'vip' },
          ],
          validation: [{ type: 'required', message: 'Veuillez sélectionner un type de pass' }],
          width: 'full',
        },
        {
          id: generateId(),
          type: 'checkboxgroup',
          name: 'sessions',
          label: 'Sessions qui vous intéressent',
          helpText: 'Sélectionnez les sessions auxquelles vous souhaitez assister',
          options: [
            { label: 'Keynote: L\'avenir de l\'IA', value: 'keynote_ia' },
            { label: 'Workshop: React avancé', value: 'workshop_react' },
            { label: 'Conférence: DevOps & Cloud', value: 'conf_devops' },
            { label: 'Table ronde: Leadership tech', value: 'table_leadership' },
            { label: 'Atelier: Cybersécurité', value: 'atelier_cyber' },
            { label: 'Networking cocktail', value: 'networking' },
          ],
          validation: [],
          width: 'full',
        },
        {
          id: generateId(),
          type: 'select',
          name: 'repas',
          label: 'Préférence alimentaire',
          placeholder: 'Sélectionnez',
          options: [
            { label: 'Standard', value: 'standard' },
            { label: 'Végétarien', value: 'vegetarien' },
            { label: 'Végan', value: 'vegan' },
            { label: 'Sans gluten', value: 'sans_gluten' },
            { label: 'Halal', value: 'halal' },
            { label: 'Casher', value: 'casher' },
          ],
          validation: [{ type: 'required', message: 'Veuillez indiquer vos préférences alimentaires' }],
          width: 'half',
        },
        {
          id: generateId(),
          type: 'textarea',
          name: 'allergies',
          label: 'Allergies ou restrictions',
          placeholder: 'Précisez si nécessaire...',
          rows: 2,
          validation: [],
          width: 'half',
        },
      ],
    },
    {
      id: generateId(),
      title: 'Informations complémentaires',
      fields: [
        {
          id: generateId(),
          type: 'radio',
          name: 'hebergement',
          label: 'Avez-vous besoin d\'aide pour l\'hébergement ?',
          options: [
            { label: 'Non, j\'ai déjà réservé', value: 'non' },
            { label: 'Oui, envoyez-moi les recommandations d\'hôtels', value: 'oui' },
          ],
          validation: [],
          width: 'full',
        },
        {
          id: generateId(),
          type: 'textarea',
          name: 'questions',
          label: 'Questions ou demandes particulières',
          placeholder: 'Posez vos questions...',
          rows: 3,
          validation: [],
          width: 'full',
        },
        {
          id: generateId(),
          type: 'checkbox',
          name: 'conditions',
          label: 'Conditions générales',
          description: 'J\'accepte les conditions générales de participation et la politique de confidentialité',
          validation: [{ type: 'required', message: 'Vous devez accepter les conditions' }],
          width: 'full',
        },
        {
          id: generateId(),
          type: 'checkbox',
          name: 'newsletter',
          label: 'Newsletter',
          description: 'Je souhaite recevoir les actualités et invitations aux prochains événements',
          validation: [],
          width: 'full',
        },
      ],
    },
  ],
  settings: {
    submitLabel: 'Confirmer mon inscription',
    successMessage: 'Votre inscription est confirmée ! Vous recevrez un email de confirmation avec tous les détails.',
    allowMultipleSubmissions: false,
    showProgressBar: true,
    showPageNumbers: true,
    showRequiredAsterisk: true,
    layout: 'card',
    labelPosition: 'top',
  },
  theme: {
    primaryColor: '#F97316',
    backgroundColor: '#FFF7ED',
    textColor: '#1F2937',
    borderRadius: 'xl',
    fontSize: 'md',
    spacing: 'relaxed',
  },
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

// =============================================================================
// 5. FORMULAIRE MÉDICAL
// =============================================================================
const medicalForm: Form = {
  id: generateId(),
  name: 'Fiche patient',
  description: 'Formulaire d\'information patient pour cabinet médical',
  sections: [
    {
      id: generateId(),
      title: 'Informations personnelles',
      description: 'Ces informations sont confidentielles et protégées',
      fields: [
        {
          id: generateId(),
          type: 'select',
          name: 'civilite',
          label: 'Civilité',
          options: [
            { label: 'Monsieur', value: 'mr' },
            { label: 'Madame', value: 'mme' },
            { label: 'Autre', value: 'autre' },
          ],
          validation: [{ type: 'required', message: 'La civilité est obligatoire' }],
          width: 'quarter',
        },
        {
          id: generateId(),
          type: 'text',
          name: 'prenom',
          label: 'Prénom',
          validation: [{ type: 'required', message: 'Le prénom est obligatoire' }],
          width: 'third',
        },
        {
          id: generateId(),
          type: 'text',
          name: 'nom',
          label: 'Nom de famille',
          validation: [{ type: 'required', message: 'Le nom est obligatoire' }],
          width: 'third',
        },
        {
          id: generateId(),
          type: 'date',
          name: 'date_naissance',
          label: 'Date de naissance',
          validation: [{ type: 'required', message: 'La date de naissance est obligatoire' }],
          width: 'half',
        },
        {
          id: generateId(),
          type: 'text',
          name: 'numero_secu',
          label: 'Numéro de sécurité sociale',
          placeholder: '1 XX XX XX XXX XXX XX',
          validation: [{ type: 'required', message: 'Le numéro de sécurité sociale est obligatoire' }],
          width: 'half',
        },
        {
          id: generateId(),
          type: 'text',
          name: 'adresse',
          label: 'Adresse',
          validation: [{ type: 'required', message: "L'adresse est obligatoire" }],
          width: 'full',
        },
        {
          id: generateId(),
          type: 'text',
          name: 'code_postal',
          label: 'Code postal',
          validation: [{ type: 'required', message: 'Le code postal est obligatoire' }],
          width: 'quarter',
        },
        {
          id: generateId(),
          type: 'text',
          name: 'ville',
          label: 'Ville',
          validation: [{ type: 'required', message: 'La ville est obligatoire' }],
          width: 'half',
        },
        {
          id: generateId(),
          type: 'phone',
          name: 'telephone',
          label: 'Téléphone',
          validation: [{ type: 'required', message: 'Le téléphone est obligatoire' }],
          width: 'half',
        },
        {
          id: generateId(),
          type: 'email',
          name: 'email',
          label: 'Email',
          validation: [],
          width: 'half',
        },
      ],
    },
    {
      id: generateId(),
      title: 'Personne à contacter en cas d\'urgence',
      fields: [
        {
          id: generateId(),
          type: 'text',
          name: 'urgence_nom',
          label: 'Nom et prénom',
          validation: [{ type: 'required', message: 'Le nom du contact d\'urgence est obligatoire' }],
          width: 'half',
        },
        {
          id: generateId(),
          type: 'text',
          name: 'urgence_lien',
          label: 'Lien de parenté',
          placeholder: 'Conjoint, Parent, Enfant...',
          validation: [{ type: 'required', message: 'Le lien de parenté est obligatoire' }],
          width: 'half',
        },
        {
          id: generateId(),
          type: 'phone',
          name: 'urgence_telephone',
          label: 'Téléphone',
          validation: [{ type: 'required', message: 'Le téléphone du contact d\'urgence est obligatoire' }],
          width: 'half',
        },
      ],
    },
    {
      id: generateId(),
      title: 'Antécédents médicaux',
      fields: [
        {
          id: generateId(),
          type: 'checkboxgroup',
          name: 'antecedents',
          label: 'Avez-vous ou avez-vous eu l\'une de ces conditions ?',
          options: [
            { label: 'Diabète', value: 'diabete' },
            { label: 'Hypertension', value: 'hypertension' },
            { label: 'Maladies cardiaques', value: 'cardiaque' },
            { label: 'Asthme', value: 'asthme' },
            { label: 'Allergies', value: 'allergies' },
            { label: 'Cancer', value: 'cancer' },
            { label: 'Maladies auto-immunes', value: 'auto_immune' },
            { label: 'Dépression / Anxiété', value: 'mental' },
          ],
          validation: [],
          width: 'full',
        },
        {
          id: generateId(),
          type: 'textarea',
          name: 'allergies_details',
          label: 'Allergies connues (médicaments, aliments, etc.)',
          placeholder: 'Listez vos allergies...',
          rows: 3,
          validation: [],
          width: 'full',
        },
        {
          id: generateId(),
          type: 'textarea',
          name: 'traitements',
          label: 'Traitements en cours',
          placeholder: 'Listez vos médicaments actuels...',
          rows: 3,
          validation: [],
          width: 'full',
        },
        {
          id: generateId(),
          type: 'textarea',
          name: 'chirurgies',
          label: 'Opérations chirurgicales passées',
          placeholder: 'Listez vos opérations avec les dates approximatives...',
          rows: 3,
          validation: [],
          width: 'full',
        },
      ],
    },
    {
      id: generateId(),
      title: 'Mode de vie',
      fields: [
        {
          id: generateId(),
          type: 'radio',
          name: 'tabac',
          label: 'Tabac',
          options: [
            { label: 'Non fumeur', value: 'non' },
            { label: 'Ancien fumeur', value: 'ancien' },
            { label: 'Fumeur occasionnel', value: 'occasionnel' },
            { label: 'Fumeur régulier', value: 'regulier' },
          ],
          validation: [],
          width: 'half',
        },
        {
          id: generateId(),
          type: 'radio',
          name: 'alcool',
          label: 'Consommation d\'alcool',
          options: [
            { label: 'Jamais', value: 'jamais' },
            { label: 'Occasionnellement', value: 'occasionnel' },
            { label: 'Régulièrement', value: 'regulier' },
          ],
          validation: [],
          width: 'half',
        },
        {
          id: generateId(),
          type: 'slider',
          name: 'activite_physique',
          label: 'Heures d\'activité physique par semaine',
          minValue: 0,
          maxValue: 20,
          step: 1,
          showValue: true,
          validation: [],
          width: 'full',
        },
      ],
    },
    {
      id: generateId(),
      title: 'Consentement',
      fields: [
        {
          id: generateId(),
          type: 'checkbox',
          name: 'consentement_donnees',
          label: 'Consentement au traitement des données',
          description: 'J\'autorise le cabinet à conserver et traiter mes données médicales conformément au RGPD',
          validation: [{ type: 'required', message: 'Le consentement est obligatoire' }],
          width: 'full',
        },
        {
          id: generateId(),
          type: 'signature',
          name: 'signature',
          label: 'Signature du patient',
          validation: [{ type: 'required', message: 'La signature est obligatoire' }],
          width: 'full',
        },
        {
          id: generateId(),
          type: 'date',
          name: 'date_signature',
          label: 'Date',
          validation: [{ type: 'required', message: 'La date est obligatoire' }],
          width: 'half',
        },
      ],
    },
  ],
  settings: {
    submitLabel: 'Enregistrer ma fiche',
    successMessage: 'Votre fiche patient a été enregistrée avec succès.',
    allowMultipleSubmissions: false,
    showProgressBar: true,
    showPageNumbers: true,
    showRequiredAsterisk: true,
    layout: 'bordered',
    labelPosition: 'top',
  },
  theme: {
    primaryColor: '#0891B2',
    backgroundColor: '#ECFEFF',
    textColor: '#1F2937',
    borderRadius: 'md',
    fontSize: 'md',
    spacing: 'normal',
  },
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

// =============================================================================
// 6. DEVIS / DEMANDE DE PRIX
// =============================================================================
const quoteRequestForm: Form = {
  id: generateId(),
  name: 'Demande de devis',
  description: 'Formulaire de demande de devis pour services',
  sections: [
    {
      id: generateId(),
      title: 'Demande de devis',
      description: 'Remplissez ce formulaire pour recevoir un devis personnalisé sous 24h.',
      fields: [
        {
          id: generateId(),
          type: 'radio',
          name: 'type_client',
          label: 'Vous êtes',
          options: [
            { label: 'Un particulier', value: 'particulier' },
            { label: 'Une entreprise', value: 'entreprise' },
            { label: 'Une association', value: 'association' },
          ],
          validation: [{ type: 'required', message: 'Ce champ est obligatoire' }],
          width: 'full',
        },
        {
          id: generateId(),
          type: 'text',
          name: 'nom_complet',
          label: 'Nom complet / Raison sociale',
          validation: [{ type: 'required', message: 'Ce champ est obligatoire' }],
          width: 'full',
        },
        {
          id: generateId(),
          type: 'email',
          name: 'email',
          label: 'Email',
          validation: [
            { type: 'required', message: "L'email est obligatoire" },
            { type: 'email', message: 'Email invalide' },
          ],
          width: 'half',
        },
        {
          id: generateId(),
          type: 'phone',
          name: 'telephone',
          label: 'Téléphone',
          validation: [{ type: 'required', message: 'Le téléphone est obligatoire' }],
          width: 'half',
        },
      ],
    },
    {
      id: generateId(),
      title: 'Votre projet',
      fields: [
        {
          id: generateId(),
          type: 'checkboxgroup',
          name: 'services',
          label: 'Services souhaités',
          options: [
            { label: 'Création de site web', value: 'site_web' },
            { label: 'Application mobile', value: 'mobile' },
            { label: 'E-commerce', value: 'ecommerce' },
            { label: 'Design / Branding', value: 'design' },
            { label: 'SEO / Marketing digital', value: 'seo' },
            { label: 'Maintenance / Support', value: 'maintenance' },
          ],
          validation: [{ type: 'required', message: 'Sélectionnez au moins un service' }],
          width: 'full',
        },
        {
          id: generateId(),
          type: 'textarea',
          name: 'description_projet',
          label: 'Description de votre projet',
          placeholder: 'Décrivez votre projet en détail : objectifs, fonctionnalités souhaitées, références...',
          rows: 5,
          validation: [
            { type: 'required', message: 'La description est obligatoire' },
            { type: 'minLength', value: 50, message: 'Minimum 50 caractères' },
          ],
          width: 'full',
        },
        {
          id: generateId(),
          type: 'select',
          name: 'budget',
          label: 'Budget estimé',
          placeholder: 'Sélectionnez une fourchette',
          options: [
            { label: 'Moins de 5 000€', value: 'moins_5k' },
            { label: '5 000€ - 10 000€', value: '5k_10k' },
            { label: '10 000€ - 25 000€', value: '10k_25k' },
            { label: '25 000€ - 50 000€', value: '25k_50k' },
            { label: 'Plus de 50 000€', value: 'plus_50k' },
            { label: 'À définir', value: 'a_definir' },
          ],
          validation: [{ type: 'required', message: 'Le budget est obligatoire' }],
          width: 'half',
        },
        {
          id: generateId(),
          type: 'select',
          name: 'delai',
          label: 'Délai souhaité',
          placeholder: 'Sélectionnez',
          options: [
            { label: 'Urgent (moins d\'1 mois)', value: 'urgent' },
            { label: '1 à 3 mois', value: '1_3_mois' },
            { label: '3 à 6 mois', value: '3_6_mois' },
            { label: 'Plus de 6 mois', value: 'plus_6_mois' },
            { label: 'Pas de deadline', value: 'pas_deadline' },
          ],
          validation: [{ type: 'required', message: 'Le délai est obligatoire' }],
          width: 'half',
        },
        {
          id: generateId(),
          type: 'file',
          name: 'documents',
          label: 'Documents complémentaires',
          helpText: 'Cahier des charges, maquettes, références... (PDF, images)',
          maxFiles: 5,
          maxFileSize: 10,
          validation: [],
          width: 'full',
        },
      ],
    },
    {
      id: generateId(),
      title: 'Préférences de contact',
      fields: [
        {
          id: generateId(),
          type: 'radio',
          name: 'mode_contact',
          label: 'Comment préférez-vous être contacté ?',
          options: [
            { label: 'Par email', value: 'email' },
            { label: 'Par téléphone', value: 'telephone' },
            { label: 'Par visioconférence', value: 'visio' },
          ],
          validation: [{ type: 'required', message: 'Sélectionnez un mode de contact' }],
          width: 'full',
        },
        {
          id: generateId(),
          type: 'checkboxgroup',
          name: 'disponibilites',
          label: 'Vos disponibilités',
          options: [
            { label: 'Matin (9h-12h)', value: 'matin' },
            { label: 'Après-midi (14h-18h)', value: 'aprem' },
            { label: 'Soirée (18h-20h)', value: 'soir' },
          ],
          validation: [],
          width: 'full',
        },
        {
          id: generateId(),
          type: 'checkbox',
          name: 'rgpd',
          label: 'Protection des données',
          description: 'J\'accepte que mes données soient utilisées pour traiter ma demande de devis',
          validation: [{ type: 'required', message: 'Vous devez accepter' }],
          width: 'full',
        },
      ],
    },
  ],
  settings: {
    submitLabel: 'Envoyer ma demande',
    successMessage: 'Votre demande de devis a bien été envoyée. Nous vous répondrons sous 24h ouvrées.',
    allowMultipleSubmissions: true,
    showProgressBar: true,
    showPageNumbers: true,
    showRequiredAsterisk: true,
    layout: 'card',
    labelPosition: 'top',
  },
  theme: {
    primaryColor: '#6366F1',
    backgroundColor: '#EEF2FF',
    textColor: '#1F2937',
    borderRadius: 'lg',
    fontSize: 'md',
    spacing: 'relaxed',
  },
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

// =============================================================================
// EXPORT ALL TEMPLATES
// =============================================================================
export const FORM_TEMPLATES: FormTemplate[] = [
  {
    id: 'contact',
    name: 'Formulaire de contact',
    description: 'Formulaire de contact professionnel avec validation RGPD',
    category: 'contact',
    icon: 'Mail',
    form: contactForm,
  },
  {
    id: 'job-application',
    name: 'Candidature emploi',
    description: 'Formulaire complet de candidature avec CV et lettre de motivation',
    category: 'hr',
    icon: 'Briefcase',
    form: jobApplicationForm,
  },
  {
    id: 'satisfaction-survey',
    name: 'Enquête de satisfaction',
    description: 'Questionnaire NPS et évaluation par matrice',
    category: 'survey',
    icon: 'Star',
    form: satisfactionSurvey,
  },
  {
    id: 'event-registration',
    name: 'Inscription événement',
    description: 'Inscription à une conférence avec choix des sessions',
    category: 'event',
    icon: 'Calendar',
    form: eventRegistrationForm,
  },
  {
    id: 'medical-form',
    name: 'Fiche patient',
    description: 'Formulaire médical complet avec signature',
    category: 'healthcare',
    icon: 'Heart',
    form: medicalForm,
  },
  {
    id: 'quote-request',
    name: 'Demande de devis',
    description: 'Formulaire de demande de devis pour services',
    category: 'business',
    icon: 'FileText',
    form: quoteRequestForm,
  },
];

export const TEMPLATE_CATEGORIES = {
  contact: { label: 'Contact', labelFr: 'Contact' },
  hr: { label: 'HR', labelFr: 'Ressources Humaines' },
  survey: { label: 'Survey', labelFr: 'Sondages' },
  event: { label: 'Event', labelFr: 'Événements' },
  business: { label: 'Business', labelFr: 'Business' },
  healthcare: { label: 'Healthcare', labelFr: 'Santé' },
};
